import React from "react"
import { List } from "@cd2/components"
import { doo, titleize } from "@cd2/tools"
import { LabelInfo } from "../Components/LabelInfo"
import { QuickToolTip } from "../Components/QuickTooltip"
import { getOrderRequestStoredStatusDescription } from "../Pages/OrderRequests/orderRequestStatusTypes"
import { IRecord } from "../types/schema"
import { HoverOrderLoader } from "./HoverOrderLoader"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { buildRoute, eRoutes } from "../routes"

type IOrderRequest = IRecord<
  "orderRequests",
  "identifier" | "updated_at" | "status" | "statusOverride"
> & {
  items: IRecord<"orderRequestItems", "price_pennies" | "stock_code" | "body" | "quantity">[]
  created_by_branch: IRecord<"branches", "name">
  received_by_branch: IRecord<"branches", "name">
  order?: IRecord<"orders", "identifier"> | null
  purchase_order?: {
    sourceOrder?: IRecord<"orders", "identifier"> | null
  } | null
}

interface IProps {
  id: string
  initialData?: IOrderRequest
  renderInlineLink?: "block" | "inline"
}

export function HoverOrderRequestLoader({ id, initialData, renderInlineLink }: IProps) {
  const skeem = window.skeem

  async function loadData() {
    const quote = await skeem.fetchOne<IOrderRequest>({
      orderRequests: {
        attributes: [
          "identifier",
          "updated_at",
          "status",
          "statusOverride",
          {
            name: "created_by_branch",
            attributes: ["name"],
          },
          {
            name: "received_by_branch",
            attributes: ["name"],
          },
          {
            name: "order",
            attributes: ["identifier"],
          },
          {
            name: "purchase_order",
            attributes: [{ name: "sourceOrder", attributes: ["identifier"] }],
          },
          {
            name: "items",
            attributes: ["price_pennies", "stock_code", "body", "quantity"],
          },
        ],
        filter: { matches: { id } },
      },
    })
    return quote
  }

  return (
    <HoverResourceLoader
      reloadKey={id}
      loadData={loadData}
      initialData={initialData}
      viewLink={order => buildRoute(eRoutes["/order-requests/:id/preview"], { id: order.id })}
      renderInlineLink={renderInlineLink}
      resourceName="Order Request"
      renderHoverContentTitle={record => record.identifier}
      renderHoverContent={record => (
        <div>
          <List>
            <LabelInfo labelWidth={80} label="Identifier" value={record.identifier} inline />
            <LabelInfo
              labelWidth={80}
              label="Status"
              value={doo(() => {
                const displayStatus = record.statusOverride || record.status
                const storedDescription = getOrderRequestStoredStatusDescription(record.status)
                const tooltip = record.statusOverride
                  ? `Overridden. Stored status is ${
                      record.status ? titleize(record.status) : "unset"
                    }: ${storedDescription}`
                  : storedDescription
                return (
                  <QuickToolTip content={tooltip}>
                    <span>{displayStatus ? titleize(displayStatus) : "-"}</span>
                  </QuickToolTip>
                )
              })}
              inline
            />
            {record.purchase_order?.sourceOrder && (
              <LabelInfo
                labelWidth={80}
                label="Source order"
                value={<HoverOrderLoader id={record.purchase_order.sourceOrder.id} />}
                inline
              />
            )}
            {record.order && (
              <LabelInfo
                labelWidth={80}
                label="Hub order"
                value={<HoverOrderLoader id={record.order.id} />}
                inline
              />
            )}
            {record.created_by_branch && (
              <LabelInfo
                labelWidth={80}
                label="Source Branch"
                value={record.created_by_branch.name}
                inline
              />
            )}
            {record.received_by_branch && (
              <LabelInfo
                labelWidth={80}
                label="Target Branch"
                value={record.received_by_branch.name}
                inline
              />
            )}
          </List>
        </div>
      )}
      renderTarget={record => <span>{record.identifier}</span>}
    />
  )
}
